import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import TaskSkeleton from "./TaskSkeleton";
import ExpenseSearchSelect, { mapToSelectOptions } from "./ExpenseSearchSelect";
import "./ExpenseModule.css";
import {
    FY_MONTHS,
    buildFinancialYearOptions,
    formatFinancialYear,
    getFinancialYearStart,
} from "../utils/financialYear";

const emptyMonthBudgets = () => FY_MONTHS.map((m) => ({ month: m.value, month_budget: "" }));

export default function ExpenseBudgetList() {
    const { http, user } = AuthUser();
    const [heads, setHeads] = useState([]);
    const [budgets, setBudgets] = useState([]);
    const [filterHeadId, setFilterHeadId] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [headId, setHeadId] = useState("");
    const [year, setYear] = useState(getFinancialYearStart());
    const [yearBudget, setYearBudget] = useState("");
    const [monthBudgets, setMonthBudgets] = useState(emptyMonthBudgets());
    const yearOptions = buildFinancialYearOptions();

    useEffect(() => {
        http.get(`/expense_heads?login_user=${user.id}`)
            .then((res) => { if (res.data.success) setHeads(res.data.data || []); })
            .catch(() => toast.error("Error loading categories"));
    }, []);

    useEffect(() => { fetchBudgets(); }, [filterHeadId]);

    const fetchBudgets = async () => {
        setIsLoading(true);
        try {
            let url = `/expense_budgets?login_user=${user.id}`;
            if (filterHeadId) url += `&head_id=${filterHeadId}`;
            const res = await http.get(url);
            if (res.data.success) setBudgets(res.data.data || []);
        } catch (err) {
            toast.error("Error fetching budgets");
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setEditId(null);
        setHeadId(filterHeadId || "");
        setYear(getFinancialYearStart());
        setYearBudget("");
        setMonthBudgets(emptyMonthBudgets());
        setShowForm(false);
    };

    const openAdd = () => {
        if (!heads.length) {
            toast.error("Add categories first");
            return;
        }
        resetForm();
        setShowForm(true);
    };

    const openEdit = (budget) => {
        setEditId(budget.id);
        setHeadId(String(budget.expense_head_id || ""));
        setYear(budget.year);
        setYearBudget(budget.year_budget || "");
        const map = {};
        (budget.month_budgets || []).forEach((mb) => { map[mb.month] = mb.month_budget; });
        setMonthBudgets(FY_MONTHS.map((m) => ({ month: m.value, month_budget: map[m.value] ?? "" })));
        setShowForm(true);
    };

    const handleMonthChange = (month, value) => {
        setMonthBudgets((prev) => prev.map((row) => row.month === month ? { ...row, month_budget: value } : row));
    };

    const buildPayload = () => ({
        expense_head_id: parseInt(headId, 10),
        year: parseInt(year, 10),
        year_budget: parseFloat(yearBudget) || 0,
        months: monthBudgets.map((row) => ({
            month: row.month,
            month_budget: parseFloat(row.month_budget) || 0,
        })),
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!headId) {
            toast.error("Please select a category");
            return;
        }
        try {
            const payload = buildPayload();
            const res = editId
                ? await http.put(`/expense_budget/${editId}`, payload)
                : await http.post(`/expense_budget?login_user=${user.id}`, payload);
            if (res.data.success) {
                toast.success(editId ? "Budget updated!" : "Budget created!");
                resetForm();
                fetchBudgets();
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error saving budget");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this budget?")) return;
        try {
            const res = await http.delete(`/expense_budget/${id}`);
            if (res.data.success) {
                toast.success("Deleted!");
                fetchBudgets();
            }
        } catch (err) {
            toast.error("Error deleting");
        }
    };

    const fmt = (v) => parseFloat(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const categoryOptions = mapToSelectOptions(heads);
    const formCategoryOptions = mapToSelectOptions(heads);
    const yearOptionsList = yearOptions.map((y) => ({ value: String(y), label: formatFinancialYear(y) }));
    const formMonthTotal = monthBudgets.reduce((sum, row) => sum + (parseFloat(row.month_budget) || 0), 0);
    const firstHalfMonths = FY_MONTHS.slice(0, 6);
    const secondHalfMonths = FY_MONTHS.slice(6);

    const renderMonthRows = (months) => (
        <div className="expense-month-rows">
            {months.map((m) => {
                const row = monthBudgets.find((r) => r.month === m.value);
                return (
                    <div className="expense-month-row" key={m.value}>
                        <label className="expense-month-label" htmlFor={`month-budget-${m.value}`}>
                            {m.short}
                        </label>
                        <input
                            id={`month-budget-${m.value}`}
                            type="number"
                            step="0.01"
                            min="0"
                            className="form-control form-control-sm"
                            value={row?.month_budget ?? ""}
                            onChange={(e) => handleMonthChange(m.value, e.target.value)}
                            placeholder="0.00"
                        />
                    </div>
                );
            })}
        </div>
    );

    return (
        <>
            <Toaster />
            <div className="container mt-3 expense-module">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h5 className="fw-bold mb-0">Category Budget (Year / Month)</h5>
                        <small className="text-muted">Indian financial year: April to March</small>
                    </div>
                    <button className="btn btn-sm btn-success" onClick={openAdd}><i className="fas fa-plus me-1"></i>Add Budget</button>
                </div>

                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label small fw-semibold mb-1">Filter by Category</label>
                                <ExpenseSearchSelect
                                    options={categoryOptions}
                                    value={filterHeadId}
                                    onChange={setFilterHeadId}
                                    placeholder="Search category..."
                                    isClearable
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {showForm && (
                    <div className="card mb-4 border-0 shadow-sm">
                        <div
                            className="card-header py-2 text-white"
                            style={{ background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)" }}
                        >
                            <strong>{editId ? "Edit Budget" : "Add Budget"}</strong>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row g-2 mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label small fw-semibold mb-1">Category *</label>
                                        <ExpenseSearchSelect
                                            options={formCategoryOptions}
                                            value={headId}
                                            onChange={setHeadId}
                                            placeholder="Search category..."
                                            isDisabled={!!editId}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label small fw-semibold mb-1">Financial Year *</label>
                                        <ExpenseSearchSelect
                                            options={yearOptionsList}
                                            value={String(year)}
                                            onChange={setYear}
                                            placeholder="Search year..."
                                            isDisabled={!!editId}
                                            isSearchable
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label small fw-semibold mb-1">Year Budget</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            className="form-control form-control-sm text-end"
                                            value={yearBudget}
                                            onChange={(e) => setYearBudget(e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <label className="form-label small fw-bold mb-0">Monthly Budgets (Apr – Mar)</label>
                                    <span className="badge bg-light text-dark border">
                                        Monthly total: {fmt(formMonthTotal)}
                                    </span>
                                </div>

                                <div className="expense-month-grid">
                                    <div className="expense-month-group">
                                        <div className="expense-month-group-title">Apr – Sep</div>
                                        {renderMonthRows(firstHalfMonths)}
                                    </div>
                                    <div className="expense-month-group">
                                        <div className="expense-month-group-title">Oct – Mar</div>
                                        {renderMonthRows(secondHalfMonths)}
                                    </div>
                                </div>

                                <div className="mt-3 d-flex gap-2">
                                    <button type="submit" className="btn btn-sm btn-primary">
                                        <i className="fas fa-check me-1"></i>{editId ? "Update" : "Save"}
                                    </button>
                                    <button type="button" className="btn btn-sm btn-secondary" onClick={resetForm}>Cancel</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {isLoading ? <TaskSkeleton /> : (
                    <table className="table table-striped table-sm">
                        <thead className="table-primary">
                            <tr>
                                <th>#</th>
                                <th>Category</th>
                                <th>Financial Year</th>
                                <th>Year Budget</th>
                                <th>Monthly Total</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {budgets.length === 0 ? (
                                <tr><td colSpan={6} className="text-center text-muted py-3">No budgets yet. Select category and add budget.</td></tr>
                            ) : budgets.map((b, idx) => {
                                const monthTotal = (b.month_budgets || []).reduce((s, m) => s + parseFloat(m.month_budget || 0), 0);
                                return (
                                    <tr key={b.id}>
                                        <td>{idx + 1}</td>
                                        <td><span className="badge bg-light text-dark border">{b.head?.name || "—"}</span></td>
                                        <td><strong>{b.financial_year_label || formatFinancialYear(b.year)}</strong></td>
                                        <td>{fmt(b.year_budget)}</td>
                                        <td>{fmt(monthTotal)}</td>
                                        <td>
                                            <button className="btn btn-sm btn-outline-primary px-2 me-1" onClick={() => openEdit(b)}><i className="fas fa-edit"></i></button>
                                            <button className="btn btn-sm btn-outline-danger px-2" onClick={() => handleDelete(b.id)}><i className="fas fa-trash"></i></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
}
